import { Navigation, FileCheck, MessageSquare, Check, ArrowRight, Image as ImageIcon } from "lucide-react";
import { LocaleLink } from "../i18n/LocaleLink";
import { MotionProvider, Reveal, type AlertItem } from "../ds/core";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";



const WORKER = "https://framerusercontent.com/images/gvrZUoowU6t5rwpwtONkkVWoFSU.png";

const CARD = "flex h-full flex-col rounded-2xl bg-bg-subtle p-4 md:p-7 font-ds shadow-[0_1px_3px_rgba(15,23,42,0.05)]";
/** equal-height animation panel for the bottom row */
const PANEL = "relative mt-6 h-[132px] overflow-hidden rounded-xl bg-surface";

function Head({ title, body }: { title: string; body: string }) {
  return (
    <>
      <h3 className="text-ds-22 font-semibold tracking-tight text-ds-ink">{title}</h3>
      <p className="mt-2 text-ds-14 leading-relaxed text-ds-ink-soft">{body}</p>
    </>
  );
}

/**
 * Real app screenshots per interface language — the UI ships in Dutch, French
 * and English, so every other locale falls back to English.
 * `NAVIGATE` is cropped around the live-position dot: the source is a full
 * phone screen and this card only has room for the map.
 */
const VERIFY_IMG = {
  nl: "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_900/v1784703912/verification_dutch_bwjpcy.png",
  "fr-be": "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_900/v1784703913/verification_french_izhb00.png",
  en: "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_900/v1784703913/Verification_english_ksvf6i.png",
} as const;
const OVERTIME_IMG = {
  nl: "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_1000/v1784703911/Overtime_dutch_yi9imo.png",
  "fr-be": "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_1000/v1784703912/Overtime_french_owqdxh.png",
  en: "https://res.cloudinary.com/lbx0aw1r/image/upload/f_auto,q_auto,w_1000/v1784703912/Overtime_engllsh_f0ty4m.png",
} as const;
const NAVIGATE_IMG = {
  nl: "https://res.cloudinary.com/lbx0aw1r/image/upload/c_crop,x_53,y_515,w_820,h_455/f_auto,q_auto,w_700/v1784703911/Navigate_dutch_wpbpl4.png",
  "fr-be": "https://res.cloudinary.com/lbx0aw1r/image/upload/c_crop,x_53,y_520,w_824,h_457/f_auto,q_auto,w_700/v1784703911/Navigate_french_u3mo5y.png",
  en: "https://res.cloudinary.com/lbx0aw1r/image/upload/c_crop,x_33,y_561,w_822,h_456/f_auto,q_auto,w_700/v1784703911/Navigate_english_yuhji4.png",
} as const;
const pick = <T,>(m: Record<string, T>, locale: string, fallback: T): T => m[locale] ?? fallback;

export default function TimeBento() {
  const { market, locale, t } = useLocale();
  const payroll = market === "belgium"
    ? t("home:timeBento.payroll.body.be")
    : t("home:timeBento.payroll.body.intl");
  const provider = market === "belgium" ? t("home:timeBento.payroll.provider.be") : t("home:timeBento.payroll.provider.intl");
  const MESSAGES: AlertItem[] = [
    { icon: MessageSquare, title: t("home:timeBento.messaging.items.0.title"), sub: t("home:timeBento.messaging.items.0.sub") },
    { icon: ImageIcon, title: t("home:timeBento.messaging.items.1.title"), sub: t("home:timeBento.messaging.items.1.sub") },
    { icon: Navigation, title: t("home:timeBento.messaging.items.2.title"), sub: t("home:timeBento.messaging.items.2.sub") },
  ];

  return (
    <MotionProvider>
      <section className="bg-bg font-ds">
        <div className="mx-auto max-w-content px-4 py-16 md:px-10 md:py-20">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <p className="text-ds-14 font-semibold uppercase tracking-[0.14em] text-ink-faint">{t("home:timeBento.heading")}</p>
            <LocaleLink to="/features" className="hidden items-center gap-1 text-ds-14 font-semibold text-ds-accent hover:underline md:inline-flex">
              {t("home:timeBento.allFeatures")} <ArrowRight size={15} strokeWidth={2} />
            </LocaleLink>
          </div>

          {/* one connected white block; grey cards inside */}
          <div className="mt-10 rounded-3xl border border-line bg-surface p-2">
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-6">
              {/* ROW 1 — clean photo (left, bigger) */}
              <Reveal className="min-w-0 md:col-span-2 lg:col-span-3">
                <div className="h-full min-h-[300px] overflow-hidden rounded-2xl">
                  <img src={cmsImage(t, "home:timeBento.photo", WORKER)} alt={t("home:timeBento.photo.alt")} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                </div>
              </Reveal>

              {/* ROW 1 — Location-verified check-in (right, smaller): the app's
                  verification table — start/end each location-checked, + duration. */}
              <Reveal delay={0.06} className="min-w-0 lg:col-span-3">
                <div className={CARD}>
                  <Head title={t("home:timeBento.location.title")} body={t("home:timeBento.location.body")} />
                  {/* The app's own verification table: start and end each
                      location-checked, beside the duration. */}
                  <img
                    src={pick(VERIFY_IMG, locale, VERIFY_IMG.en)}
                    alt={t("home:timeBento.location.alt")}
                    width={900} height={710}
                    loading="lazy" decoding="async"
                    className="mt-6 w-full rounded-xl"
                  />
</div>
              </Reveal>

              {/* ROW 2 — Overtime register: per-row Free time / Paid compensation. */}
              <Reveal delay={0.04} className="min-w-0 lg:col-span-4">
                <div className={CARD}>
                  <Head title={t("home:timeBento.overtime.title")} body={t("home:timeBento.overtime.body")} />
                  {/* The app's overtime register — compensation type per row. */}
                  <img
                    src={pick(OVERTIME_IMG, locale, OVERTIME_IMG.en)}
                    alt={t("home:timeBento.overtime.alt")}
                    width={1000} height={355}
                    loading="lazy" decoding="async"
                    className="mt-6 w-full rounded-xl"
                  />
</div>
              </Reveal>
              <Reveal delay={0.08} className="min-w-0 lg:col-span-2">
                <div className={CARD}>
                  <Head title={t("home:timeBento.messaging.title")} body={t("home:timeBento.messaging.body")} />
                  <div className="mt-6 flex flex-1 flex-col gap-2.5">
                    {MESSAGES.map((m, i) => {
                      const Icon = m.icon;
                      return (
                        <div key={i} className="flex items-center gap-3 rounded-xl bg-surface p-3">
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-ds-tint text-ds-accent"><Icon size={15} strokeWidth={2} /></span>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-[12px] font-semibold text-ds-ink">{m.title}</div>
                            <div className="truncate text-[12px] text-ds-ink-soft">{m.sub}</div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="mt-auto flex items-center gap-2 pl-1 pt-1">
                      <span className="flex gap-1">
                        <span className="ds-blink h-1.5 w-1.5 rounded-full bg-ds-ink-soft" />
                        <span className="ds-blink h-1.5 w-1.5 rounded-full bg-ds-ink-soft" style={{ animationDelay: "0.2s" }} />
                        <span className="ds-blink h-1.5 w-1.5 rounded-full bg-ds-ink-soft" style={{ animationDelay: "0.4s" }} />
                      </span>
                      <span className="text-[11px] text-ds-ink-soft">{t("home:timeBento.messaging.typing")}</span>
                    </div>
                  </div>
                </div>
              </Reveal>

              {/* ROW 3 — three equal cards, equal-height animations */}
              <Reveal delay={0.04} className="min-w-0 lg:col-span-2">
                <div className={CARD}>
                  <Head title={t("home:timeBento.leave.title")} body={t("home:timeBento.leave.body")} />
                  <div className={`${PANEL} flex items-center px-4`}>
                    <div className="grid w-full grid-cols-5 gap-1.5 text-center">
                      {["M", "T", "W", "T", "F"].map((d, i) => (
                        <span key={i} className="text-[9px] font-semibold uppercase text-ds-ink-soft">{d}</span>
                      ))}
                      {[12, 13, 14, 15, 16].map((n) => (
                        <span key={n} className="relative grid h-9 place-items-center overflow-hidden rounded-md bg-bg-subtle text-[12px] font-semibold text-ds-ink">
                          {n === 14 && <span className="ds-book absolute inset-0 rounded-md bg-ds-tint" aria-hidden />}
                          <span className={`relative z-10 ${n === 14 ? "text-ds-accent" : ""}`}>{n}</span>
                          {n === 14 && (
                            <svg viewBox="0 0 40 36" fill="none" aria-hidden className="absolute left-1/2 top-1/2 z-10 h-9 w-10 -translate-x-1/2 -translate-y-1/2">
                              <ellipse className="ds-circle-draw" pathLength={100} cx="20" cy="18" rx="15" ry="13" stroke="var(--ds-accent)" strokeWidth="2.5" strokeLinecap="round" transform="rotate(-8 20 18)" />
                            </svg>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.06} className="min-w-0 lg:col-span-2">
                <div className={CARD}>
                  <Head title={t("home:timeBento.navigate.title")} body={t("home:timeBento.navigate.body")} />
                  {/* The app's Trip view, cropped to the map around the live
                      position dot. */}
                  <img
                    src={pick(NAVIGATE_IMG, locale, NAVIGATE_IMG.en)}
                    alt={t("home:timeBento.navigate.alt")}
                    width={700} height={389}
                    loading="lazy" decoding="async"
                    className="mt-6 w-full rounded-xl"
                  />
</div>
              </Reveal>
              <Reveal delay={0.08} className="min-w-0 lg:col-span-2">
                <div className={CARD}>
                  <Head title={t("home:timeBento.payroll.title")} body={payroll} />
                  <div className={`${PANEL} flex flex-col justify-center gap-3 px-4`}>
                    <div className="flex items-center gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-ds-tint text-ds-accent"><FileCheck size={17} strokeWidth={2} /></span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[12px] font-semibold text-ds-ink">timesheet-june.csv</div>
                        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-bg-subtle"><div className="ds-fill h-full rounded-full bg-ds-accent" /></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-bg-subtle px-3 py-2">
                      <span className="text-[11px] font-medium text-ds-ink-soft">{provider}</span>
                      <span className="ds-sync-in inline-flex items-center gap-1 text-[11px] font-semibold text-ds-pos"><span className="grid h-3.5 w-3.5 place-items-center rounded-full bg-ds-pos text-white"><Check size={9} strokeWidth={3.5} /></span> Synced</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </MotionProvider>
  );
}
